import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BedDouble, Bath, Maximize, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/site/PropertyCard";
import { InquiryForm } from "@/components/site/InquiryForm";
import { Reveal, MaskLine, Eyebrow } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { useLang } from "@/lib/i18n";
import { api, useSite, mediaUrl, formatPrice, locationOf } from "@/lib/site";
import { WaButton } from "@/components/site/WhatsApp";

const Video = ({ url }) => {
  const yt = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  const vm = url.match(/vimeo\.com\/(\d+)/);
  const src = yt ? `https://www.youtube.com/embed/${yt[1]}` : vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
  return (
    <div className="aspect-video bg-midnight" data-testid="property-video">
      {src ? <iframe title="video" src={src} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen /> : <video src={mediaUrl(url)} controls playsInline className="w-full h-full object-cover" />}
    </div>
  );
};

export default function PropertyDetail() {
  const { slug } = useParams();
  const { t, tr, lang } = useLang();
  const { countries } = useSite();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [lb, setLb] = useState(null);
  useEffect(() => { api.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <main className="min-h-screen bg-ivory pt-40 px-6 text-center font-display text-3xl text-midnight">404 · <Link to="/nehnutelnosti" className="text-gold">{t.back}</Link></main>;
  if (!p) return <main className="min-h-screen bg-midnight" />;
  const imgs = p.images || [];
  const loc = locationOf(p, countries, tr);
  const price = formatPrice(p, lang) || t.priceOnRequest;
  const jsonLd = { "@context": "https://schema.org", "@type": "RealEstateListing", name: tr(p.title), description: tr(p.description), image: imgs.map(mediaUrl), offers: p.price ? { "@type": "Offer", price: p.price, priceCurrency: p.currency } : undefined };
  return (
    <main data-testid="property-detail-page" className="bg-ivory">
      <Seo title={tr(p.title)} description={tr(p.description).slice(0, 160)} image={mediaUrl(imgs[0])} path={`/nehnutelnosti/${p.slug}`} jsonLd={jsonLd} />
      <section className="relative h-[88svh] min-h-[560px] overflow-hidden bg-midnight">
        <motion.img initial={{ scale: 1.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }} src={mediaUrl(imgs[0])} alt={tr(p.title)} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/20 to-midnight/40" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-14">
          <Link to="/nehnutelnosti" data-testid="back-to-listings" className="text-[11px] tracking-[0.3em] uppercase text-ivory/80 hover:text-gold flex items-center gap-2 mb-8"><ArrowLeft size={14} /> {t.back}</Link>
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="bg-gold text-midnight text-[10px] tracking-[0.25em] uppercase px-3 py-2">{t.status[p.status]}</span>
            <Badge text={p.badge} />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-ivory leading-[1.03] max-w-4xl" data-testid="property-title"><MaskLine animateNow delay={0.4}>{tr(p.title)}</MaskLine></h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-10 gap-y-3 text-ivory/90">
            <span className="flex items-center gap-2 text-sm"><MapPin size={15} strokeWidth={1.3} className="text-gold" />{loc}</span>
            <span className="font-display text-3xl text-gold" data-testid="property-price">{price}</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="property-wa-ctas">
            <WaButton testid="wa-viewing" text={`${t.waGreeting}: ${t.viewing} – ${tr(p.title)} (${window.location.href})`} label={t.viewing} />
            <WaButton testid="wa-invest" className="btn-ghost" text={`${t.waGreeting}: ${t.invest} – ${tr(p.title)} (${window.location.href})`} label={t.invest} />
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <div className="grid grid-cols-3 border-y border-sand py-8 mb-14" data-testid="property-specs">
            {[[BedDouble, t.beds, p.bedrooms], [Bath, t.baths, p.bathrooms], [Maximize, t.area, p.area ? `${p.area} m²` : "—"]].map(([I, l, v]) => (
              <div key={l} className="text-center">
                <I size={20} strokeWidth={1.1} className="mx-auto text-gold" />
                <div className="font-display text-3xl text-midnight mt-3">{v || "—"}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-midnight/60 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <Eyebrow className="text-midnight/70 mb-6">{t.description}</Eyebrow>
          <Reveal><p className="text-midnight/85 leading-[1.9] whitespace-pre-line text-base" data-testid="property-description">{tr(p.description)}</p></Reveal>
          {imgs.length > 1 && (
            <div className="mt-16">
              <Eyebrow className="text-midnight/70 mb-6">{t.gallery}</Eyebrow>
              <div className="grid grid-cols-2 gap-3" data-testid="property-gallery">
                {imgs.map((src, k) => (
                  <motion.button key={src + k} onClick={() => setLb(k)} data-testid={`gallery-image-${k}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8, delay: (k % 2) * 0.1 }}
                    className={`overflow-hidden group ${k % 3 === 0 ? "col-span-2 aspect-[16/9]" : "aspect-square"}`}>
                    <img src={mediaUrl(src)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
                  </motion.button>
                ))}
              </div>
            </div>
          )}
          {p.reels?.length > 0 && (
            <div className="mt-16">
              <Eyebrow className="text-midnight/70 mb-6">Reels</Eyebrow>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="property-reels">
                {p.reels.map((u, k) => <video key={u + k} src={mediaUrl(u)} autoPlay muted loop playsInline controls className="w-full aspect-[9/16] object-cover bg-black" />)}
              </div>
            </div>
          )}
          {p.video_url && <div className="mt-16"><Eyebrow className="text-midnight/70 mb-6">{t.video}</Eyebrow><Video url={p.video_url} /></div>}
        </div>
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-midnight p-8 sm:p-10" data-testid="inquiry-panel">
            <div className="flex items-center justify-between mb-8">
              <span className="font-display text-ivory text-xl">MEVA<span className="text-gold">LUX</span> <span className="text-xs tracking-[0.4em] text-ivory/70">PROPERTY</span></span>
              <span className="w-8 h-px bg-gold" />
            </div>
            <h2 className="font-display text-3xl text-ivory mb-2">{t.inquiry.title}</h2>
            <p className="text-sm text-ivory/70 mb-8">{tr(p.title)}</p>
            <InquiryForm propertyId={p.id} dark />
            <div className="mt-6 pt-6 border-t border-ivory/10"><WaButton testid="wa-panel" className="btn-ghost w-full" text={`${t.waGreeting}: ${tr(p.title)} (${window.location.href})`} label="WhatsApp" /></div>
          </div>
        </aside>
      </section>

      <Dialog open={lb !== null} onOpenChange={(o) => !o && setLb(null)}>
        <DialogContent className="max-w-6xl p-0 bg-midnight border-gold/30" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{tr(p.title)}</DialogTitle>
          {lb !== null && <img src={mediaUrl(imgs[lb])} alt="" className="w-full max-h-[85vh] object-contain" onClick={() => setLb((lb + 1) % imgs.length)} />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
